import type React from "react"
import { useTranslation } from "react-i18next"

import { PerformanceTier } from "@/components/performance/performance-tier"
import { useFormatDecimal } from "@/formatters"
import { DataGridLegend, DataGridLegendItem } from "@/nzds/data-grid"

import type { PerformanceGridLegendProps } from "./performance-grid-legend.types"

/**
 * Displays a legend of the performance tiers and counts for sites
 *
 * ```tsx
 * import { PerformanceGridLegend } from "@/components/performance/performance-grid-legend"
 *
 * <PerformanceGridLegend
 *   siteCount={1}
 *   extremelyInefficientCount={1}
 *   inefficientCount={1}
 *   atBenchmarkCount={1}
 *   efficientCount={1}
 *   highlyEfficientCount={1}
 *  />
 * ```
 */
export const PerformanceGridLegend: React.FC<PerformanceGridLegendProps> = ({
  siteCount,
  extremelyInefficientCount,
  inefficientCount,
  atBenchmarkCount,
  efficientCount,
  highlyEfficientCount,
}) => {
  const { t } = useTranslation()
  const { formatDecimal: formatDecimalToInteger } = useFormatDecimal({
    precision: 0,
  })

  return (
    <DataGridLegend
      itemCount={siteCount}
      itemLabel={t("siteCountLabel", { ns: "sites" })}
    >
      <DataGridLegendItem
        data-testid="extremelyInefficient-count"
        label={<PerformanceTier tier="extremelyInefficient" />}
        value={formatDecimalToInteger(extremelyInefficientCount)}
      />

      <DataGridLegendItem
        data-testid="inefficient-count"
        label={<PerformanceTier tier="inefficient" />}
        value={formatDecimalToInteger(inefficientCount)}
      />

      <DataGridLegendItem
        data-testid="atBenchmark-count"
        label={<PerformanceTier tier="atBenchmark" />}
        value={formatDecimalToInteger(atBenchmarkCount)}
      />

      <DataGridLegendItem
        data-testid="efficient-count"
        label={<PerformanceTier tier="efficient" />}
        value={formatDecimalToInteger(efficientCount)}
      />

      <DataGridLegendItem
        data-testid="highlyEfficient-count"
        label={<PerformanceTier tier="highlyEfficient" />}
        value={formatDecimalToInteger(highlyEfficientCount)}
      />
    </DataGridLegend>
  )
}
